<template>
  <div
    :style="pageStyle"
    :class="{myCollectionIsGroupCategory: isGroupCategoryParams}">
    <div class="listPage">
      <div v-if="groupedList.length"
        class="collectionCategoryList">
        <div v-for="group in groupedList"
          :key="group.categoryId"
          class="collectionCategoryGroup"
          :style="!group.isGroupCategory ? categoryGroupFrame : null">
          <CollectionGroup v-if="group.isGroupCategory"
            :locale="locale"
            :lang="lang"
            :group="group"
            :pageId="pageId"
            :categoryId="categoryId"
            :isStaff="isStaff"
            @completeCompare="completeCompare"
            @deleteItem="deleteItem" />

          <div v-else
            class="collectionItemList"
            :style="listStyle">
            <CollectionItem v-for="item in group.items"
              :key="item.orderItemId || item.collectionItemId"
              :locale="locale"
              :categoryId="categoryId"
              :lang="lang"
              :item="item"
              :data-item="item.collectionItemId"
              :itemDisplay="itemDisplay"
              @deleteItem="deleteItem" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CollectionItem from './MyCollectionItem'
import CollectionGroup from './MyCollectionGroup'

export default {
  name: 'MyCollection',
  props: [
    'locale',
    'lang',
    'pageId',
    'categoryId',
    'isStaff'
  ],
  components: {
    CollectionItem,
    CollectionGroup
  },
  computed: {
    getMyCollectionSetting () {
      return this.$store.getters['collection/getMyCollectionSetting']
    },
    pageFrame () {
      const frame = this.$store.getters['collection/getPageFrame']
      return frame ? `${frame}px` : ''
    },
    pageStyle () {
      const frame = this.pageFrame
      return {
        padding: frame,
        paddingTop: '0',
        ...this.isGroupCategoryParams && {
          paddingBottom: '90px'
        }
      }
    },
    categoryGroupFrame () {
      const frame = this.pageFrame
      return {
        paddingTop: frame
      }
    },
    listStyle () {
      const setting = this.getMyCollectionSetting
      return {
        gridTemplateColumns: `repeat(${setting.listColumn}, 1fr)`
      }
    },
    itemDisplay () {
      const setting = this.getMyCollectionSetting
      return setting && setting.display
    },
    isGroupCategoryParams () {
      const categoryId = this.$route.params.categoryId
      const groups = this.groupedList
      const currentGroup = groups && groups.find(group => group.categoryId === categoryId)
      return currentGroup && currentGroup.isGroupCategory
    },
    isSubCategory () {
      const categoryId = this.categoryId
      const categories =
        (this.getMyCollectionSetting &&
          this.getMyCollectionSetting.category) ||
        []

      return this.findCategoryParent(categories, categoryId) !== null
    },
    myCollectionCompare () {
      return this.$store.getters['collection/getMyCollectionCompare']
    },
    getRequestMyCollection () {
      return this.$store.getters['collection/getRequestMyCollection']
    },
  },
  
  created () {
    this.init()
  },
  watch: {
    categoryId () {
      this.updateGroupedList()
    },

    isGroupCategoryParams (isGroupCategory) {
      this.$store.commit(
        'collection/setMyCollectionIsGroupCategory',
        !!isGroupCategory
      )
    },

    getRequestMyCollection (isRequest) {
      if (!isRequest) return
      this.init()
      this.$store.commit('collection/setRequestMyCollection', false)
    }
  },
  beforeDestroy () {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },
  mounted () {
    this.intervalId = setInterval(() => {
      this.getMyCollectionItems()
    }, this.intervalDur)
  },
  data () {
    return {
      intervalDur: 60000,
      intervalId: null,
      allGroupedList: [],
      groupedList: []
    }
  },
  methods: {
    async init () {
      const categories =
        this.getMyCollectionSetting &&
        this.getMyCollectionSetting.category

      const firstCategoryId = this.getFirstCategoryId(categories)
      await this.getMyCollectionItems()

      if (!this.categoryId && firstCategoryId) {
        this.$router.replace({
          params: {
            ...this.$route.params,
            categoryId: firstCategoryId
          }
        })
      }
    },
    getFirstCategoryId (categories) {
      if (!Array.isArray(categories) || !categories.length) {
        return null
      }
      const first = categories[0]
      if (Array.isArray(first.subCategory) && first.subCategory.length) {
        return this.getFirstCategoryId(first.subCategory)
      }
      return first.categoryId
    },
    findCategoryParent (categories, categoryId, parent = null) {
      for (const category of categories || []) {
        if (category.categoryId === categoryId) {
          return parent
        }

        if (category.subCategory && category.subCategory.length) {
          const found = this.findCategoryParent(
            category.subCategory,
            categoryId,
            category
          )

          if (found !== null) {
            return found
          }
        }
      }

      return null
    },
    buildGroupedListFromResponse(responseCategories, categories) {
      const flatCategories = this.flattenLeafCategories(categories)

      const responseMap = responseCategories.reduce((map, category) => {
        map[category.categoryId] = category
        return map
      }, {})

      const knownCategoryIds = new Set(
        flatCategories.map(category => category.categoryId)
      )

      const normalGroups = flatCategories
        .map(category => {
          const responseCategory = responseMap[category.categoryId]
          const items = responseCategory && responseCategory.collectionItems || []

          return {
            categoryId: category.categoryId,
            categoryLabel: category.categoryLabel,
            isVirtualCategory: false,
            isGroupCategory: this.hasSubItems(items[0]),
            items
          }
        })
        .filter(group => group.items.length > 0)

      const virtualCategories = responseCategories
        .filter(category => !knownCategoryIds.has(category.categoryId))
        .map(category => ({
          categoryId: category.categoryId,
          categoryLabel: category.categoryLabel,
          isVirtualCategory: true,
          subCategory: []
        }))

      this.$store.commit(
        'collection/setVirtualMyCollectionCategories',
        virtualCategories
      )

      const virtualGroups = responseCategories
        .filter(category => !knownCategoryIds.has(category.categoryId))
        .map(category => ({
          categoryId: category.categoryId,
          categoryLabel: category.categoryLabel,
          isVirtualCategory: true,
          isGroupCategory: this.hasSubItems(category.collectionItems[0]),
          items: category.collectionItems || []
        }))
        .filter(group => group.items.length > 0)

      return [
        ...normalGroups,
        ...virtualGroups
      ]
    },
    isVirtualCategory (categoryId) {
      const categories = this.getMyCollectionSetting && this.getMyCollectionSetting.category || []

      const knownCategoryIds = new Set(
        this.flattenLeafCategories(categories)
          .map(category => category.categoryId)
      )

      return !knownCategoryIds.has(categoryId)
    },
    async getVirtualMyCollectionCategories () {
      const res = await this.$store.dispatch(
        'collection/getMyCollectionItems',
        {
          pageId: this.pageId
          // collectionCategory 생략
        }
      )
      console.log(res)

      const responseCategories =
        Array.isArray(res && res.message)
          ? res.message
          : []

      const settingCategories = this.getMyCollectionSetting && this.getMyCollectionSetting.category || []

      const flatCategories =
        this.flattenLeafCategories(settingCategories)

      const knownCategoryIds = new Set(
        flatCategories.map(category => category.categoryId)
      )

      const virtualCategories = responseCategories
        .filter(category => {
          return !knownCategoryIds.has(category.categoryId)
        })
        .map(category => ({
          categoryId: category.categoryId,
          categoryLabel: category.categoryLabel,
          isVirtualCategory: true,
          subCategory: []
        }))

      this.$store.commit(
        'collection/setVirtualMyCollectionCategories',
        virtualCategories
      )
    },
    updateGroupedList () {
      this.groupedList = this.allGroupedList.filter(group => {
        return group.categoryId === this.categoryId
      })
    },
    async getMyCollectionItems () {
      const res = await this.$store.dispatch(
        'collection/getMyCollectionItems',
        {
          pageId: this.pageId
        }
      )

      const responseCategories =
        Array.isArray(res && res.message)
          ? res.message
          : []

      const categories =
        (this.getMyCollectionSetting &&
          this.getMyCollectionSetting.category) ||
        []

      this.allGroupedList = this.buildGroupedListFromResponse(
        responseCategories,
        categories
      )

      this.updateGroupedList()
    },
    flattenLeafCategories(categories) {
      return (categories || []).reduce((acc, category) => {
        if (category.subCategory && category.subCategory.length > 0) {
          return acc.concat(this.flattenLeafCategories(category.subCategory))
        }

        acc.push(category)
        return acc
      }, [])
    },
    hasSubItems(item) {
      const subItems = item && item.subItems

      if (Array.isArray(subItems)) {
        return subItems.length > 0
      }

      if (subItems && typeof subItems === 'object') {
        return Object.keys(subItems).length > 0
      }

      return false
    },
    async completeCompare () {
      await this.getMyCollectionItems()
      this.$store.commit('collection/clearMyCollectionCompare')
    },
    async deleteItem (orderItemId) {
      const data = {
        pageId: this.pageId,
        collectionCategory: this.categoryId,
        orderItemId: orderItemId
      }
      this.$store.commit('loading', true)
      const res = await this.$store.dispatch('collection/removeMyCollectionItem', data)
      if (res && res.status === 200) {
        this.$store.commit('collection/setToastMessage', '내 콜렉션에서 삭제되었습니다.')
        this.getMyCollectionItems()
      } else {
        alert(res)
      }
      this.$store.commit('loading', false)
    }
  }
}
</script>